'use client'

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  ListItem,
  ListItemContent,
  ListItemIcon,
  ListItemText,
} from '@dafiti/closet-ui'
import { Coupon, Edit, HelpCircle, LogOut } from '@dafiti/closet-ui/icons'
import { isCountryFeatureEnabled } from '@dafiti/feature-flag-sdk'
import { useEnvContext } from 'next-runtime-env'
import type { Locale } from '@dafiti/feature-flag-sdk'
import type { AccordionTriggerProps } from '@dafiti/closet-ui'

import { useIsClient } from '@/hooks/useIsClient'
import { useAppLayoutContext } from '@/providers/AppLayoutProvider'
import { useMessages } from '@/i18n/useMessages'
import * as track from '@/tracking/header'
import { MobileNavigationUserMenuGreetings } from '@/components/PageHeader/Menu/MobileNavigationUserMenuGreetings'
import { useAuth } from '@/hooks/useAuth'

export const MobileNavigationUserMenu = () => {
  const { options, LinkComponent } = useAppLayoutContext()
  const { auth } = useAuth()
  const t = useMessages()
  const { NEXT_PUBLIC_DEFAULT_LOCALE } = useEnvContext() as {
    NEXT_PUBLIC_DEFAULT_LOCALE: Locale
  }

  const { urls } = options

  const isClient = useIsClient()

  const menuItems = [
    {
      label: t.menu.accountLabel,
      icon: <Edit className="size-5" />,
      href: urls.customer,
      ariaLabel: t.header.account,
      onClick: () => track.userMenuAccountClick(),
    },
    ...(isCountryFeatureEnabled('coupons', NEXT_PUBLIC_DEFAULT_LOCALE)
      ? [
          {
            label: t.menu.couponsLabel,
            icon: <Coupon className="size-5" />,
            href: urls.coupons,
            ariaLabel: t.menu.couponsAriaLabel,
            onClick: () => track.userMenuCouponsClick(),
          },
        ]
      : []),
    {
      label: t.menu.helpCenterLabel,
      icon: <HelpCircle className="size-5" />,
      href: urls.help,
      ariaLabel: t.menu.helpCenterAriaLabel,
      onClick: () => track.userMenuHelpCenterClick(),
    },
    {
      label: t.menu.logoutLabel,
      icon: <LogOut className="size-5" />,
      href: urls.logout,
      ariaLabel: t.menu.logoutAriaLabel,
      onClick: () =>
        track.authenticationClick({
          contentType: 'menu',
          complement: 'logout',
        }),
    },
  ]

  if (!auth?.isLoggedIn) {
    return (
      <LinkComponent
        href={urls.login}
        aria-label={t.menu.loginAriaLabel}
        onClick={() =>
          track.authenticationClick({
            contentType: 'menu',
            complement: 'login',
          })
        }
      >
        <MobileNavigationUserMenuGreetings />
      </LinkComponent>
    )
  }

  // If the code is running on the client, we need to use a button for the trigger.
  // If it's running on the server, we can use an anchor element to allow interaction before js loads.
  const triggerProps = isClient
    ? { as: 'button' }
    : {
        href: urls.customer,
        as: 'a',
      }

  return (
    <Accordion
      as="div"
      type="single"
      collapsible
      className="lg:hidden"
      // TODO: https://dafiti.jira.com/browse/NP-949
      onValueChange={(value: string) => {
        track.userGreetingClick({
          contentType: 'menu',
          complement: value ? 'expand' : 'collapse',
        })
      }}
    >
      <AccordionItem as="div" value="user-menu" className="block w-full">
        <AccordionTrigger
          className="w-full"
          aria-label={t.menu.triggerAriaLabel}
          {...(triggerProps as AccordionTriggerProps)}
        >
          <MobileNavigationUserMenuGreetings />
        </AccordionTrigger>

        <AccordionContent
          as="div"
          className="overflow-hidden bg-gray-200" // TODO: add overflow-hidden to AccordionContent inside closet-ui
          data-testid="user-menu-content"
        >
          {/* TODO: export List component from closet-ui */}
          <ul>
            {menuItems.map(({ icon, label, href, ariaLabel, onClick }) => (
              <ListItem
                key={label}
                className="h-[48px] w-full border-b-[1px] border-b-gray-200 bg-gray-100 px-0 active:bg-gray-200"
                as={LinkComponent}
                href={href}
                aria-label={ariaLabel}
                onClick={onClick}
              >
                <div className="container flex items-center">
                  <ListItemIcon>{icon}</ListItemIcon>

                  <ListItemContent>
                    <ListItemText className="text-xs font-normal">
                      {label}
                    </ListItemText>
                  </ListItemContent>
                </div>
              </ListItem>
            ))}
          </ul>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
